import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatInCompanyTz } from "../../../utils/utils";
import { METRICS, formatMetric, metricQuality, qualityColor } from "../../../utils/netquality";
import styles from "../network_map.module.css";

function ChartTooltip({ active, payload, metric, bucket, t }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  return (
    <div style={{ background: "#1B365D", color: "#fff", borderRadius: 6, padding: "6px 10px", fontSize: 12 }}>
      <div style={{ fontWeight: 700 }}>{formatMetric(metric, point.value)}</div>
      <div>{formatInCompanyTz(point.t, bucket === "day" ? "dd/MM" : "dd/MM HH:mm")}</div>
      <div style={{ opacity: 0.8 }}>{point.n} {t("netmap.timeline.samples")}</div>
    </div>
  );
}

export default function TimelineChart({ points, bucket, metric, iface }) {
  const { t } = useTranslation();
  const data = useMemo(
    () => (points || []).map((p) => ({ t: p.t, value: p[metric], n: p.n })),
    [points, metric],
  );
  const values = data.map((d) => d.value).filter((v) => v !== null && v !== undefined);
  if (values.length === 0) {
    return <div className={styles.empty}>{t("netmap.timeline.empty")}</div>;
  }
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const color = qualityColor(metricQuality(metric, avg, iface));
  const spec = METRICS[metric];
  const domain = metric === "score" ? [0, 100] : spec.higherIsBetter ? ["auto", "auto"] : [0, "auto"];
  return (
    <div>
      <div className={styles.timelineHeader}>
        <div className={styles.sectionTitle}>{t("netmap.timeline.title")} · {t(`netmap.metrics.${metric}`)}</div>
        <div className={styles.timelineMeta}>
          {t("netmap.timeline.avg")}: <strong>{formatMetric(metric, avg)}</strong> · {data.reduce((a, d) => a + d.n, 0)} {t("netmap.timeline.samples")}
        </div>
      </div>
      <div style={{ width: "100%", height: 190 }}>
        <ResponsiveContainer width="100%" height="100%" debounce={1}>
          <AreaChart data={data} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="netmapArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.45} />
                <stop offset="100%" stopColor={color} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#eef0f3" vertical={false} />
            <XAxis
              dataKey="t"
              tick={{ fill: "#999", fontSize: 10 }}
              tickFormatter={(tick) => formatInCompanyTz(tick, bucket === "day" ? "dd/MM" : "dd/MM HH:mm")}
              minTickGap={40}
              stroke="#d9d9d9"
            />
            <YAxis domain={domain} tick={{ fill: "#999", fontSize: 10 }} stroke="#d9d9d9" width={50} />
            <Tooltip content={<ChartTooltip metric={metric} bucket={bucket} t={t} />} />
            <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill="url(#netmapArea)" connectNulls dot={{ r: 2 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
